/** Missouri commercial personal property statutory assessment ratio (33.33%). */
export const MO_ASSESSMENT_RATIO = 0.3333;

/** SolvTax performance fee on realized savings. */
export const SOLVTAX_PERFORMANCE_FEE_RATE = 0.35;

/** Default St. Louis County–area levy applied to assessed value (7%). */
export const DEFAULT_MUNICIPAL_TAX_RATE = 0.07;

const roundMoney = (value) => Math.round(value * 100) / 100;

/**
 * Convert historical / market equipment cost to Missouri assessed value.
 * Assessed Value = Original Market/Historical Cost × 0.3333
 */
export const calculateAssessedValue = (historicalCost) =>
  roundMoney(historicalCost * MO_ASSESSMENT_RATIO);

/**
 * Annual property tax owed on an assessed value.
 * Annual Tax Owed = Assessed Value × Tax Rate
 */
export const calculateAnnualTaxOwed = (assessedValue, taxRate) =>
  roundMoney(assessedValue * taxRate);

/**
 * Project savings from correcting an over-assessed county valuation.
 *
 * @param {object} params
 * @param {number} params.historicalCost - Original equipment cost basis ($)
 * @param {number} params.taxRate - Local levy on assessed value (e.g. 0.08 = 8%)
 * @param {number} params.overAssessmentVariance - Fraction the county over-assessed
 *   relative to the statutory assessed value (e.g. 0.20 = 20% too high)
 */
export const calculateProjectedSavings = ({ historicalCost, taxRate, overAssessmentVariance }) => {
  const optimizedAssessedValue = calculateAssessedValue(historicalCost);
  const originalAssessedValue = roundMoney(optimizedAssessedValue * (1 + overAssessmentVariance));

  const originalTaxOwed = calculateAnnualTaxOwed(originalAssessedValue, taxRate);
  const optimizedTaxOwed = calculateAnnualTaxOwed(optimizedAssessedValue, taxRate);
  const projectedSavings = roundMoney(originalTaxOwed - optimizedTaxOwed);

  return {
    optimizedAssessedValue,
    originalAssessedValue,
    originalTaxOwed,
    optimizedTaxOwed,
    assessmentReduction: roundMoney(originalAssessedValue - optimizedAssessedValue),
    projectedSavings: Math.max(0, projectedSavings),
  };
};

/**
 * SolvTax Fee = max(0, Projected Savings × 0.35)
 */
export const calculateSolvTaxFee = (projectedSavings) =>
  roundMoney(Math.max(0, projectedSavings) * SOLVTAX_PERFORMANCE_FEE_RATE);

export const calculateSavingsProjection = ({
  historicalCost,
  taxRate = DEFAULT_MUNICIPAL_TAX_RATE,
  overAssessmentVariance,
}) => {
  const savings = calculateProjectedSavings({
    historicalCost,
    taxRate,
    overAssessmentVariance,
  });
  const solvtaxFee = calculateSolvTaxFee(savings.projectedSavings);

  return {
    ...savings,
    cashSavings: savings.projectedSavings,
    solvtaxFee,
  };
};
